import { Component, Element, Prop, State, h } from "@stencil/core";

/**
 * @author jgw96
 * @description lazy load images: https://github.com/jgw96/st-img
 */
@Component({
  tag: "bcp-img",
  styleUrl: "bcp-img.scss",
})
export class StImg {
  @Element() el: HTMLBcpImgElement;

  @Prop() src: string;
  @Prop() alt: string;
  @Prop() fallbackSrc: string;

  @State() oldSrc: string;

  private io: IntersectionObserver;

  componentDidLoad(): void {
    this.addIntersectionObserver();
  }

  componentWillUpdate(): void {
    if (this.src !== this.oldSrc) {
      this.addIntersectionObserver();
    }
    this.oldSrc = this.src;
  }

  private handleImage() {
    const image: HTMLImageElement = this.el.querySelector("img");
    image.setAttribute("src", image.getAttribute("data-src"));
    image.onload = () => {
      image.removeAttribute("data-src");
    };
    if (this.fallbackSrc) {
      image.onerror = () => {
        if (image.src !== this.fallbackSrc) {
          image.setAttribute("src", this.fallbackSrc);
        }
      };
    }
  }

  private addIntersectionObserver() {
    if (!this.src) {
      return;
    }
    if ("IntersectionObserver" in window) {
      this.io = new IntersectionObserver((data: any) => {
        // because there will only ever be one instance
        // of the element we are observing
        // we can just use data[0]
        if (data[0].isIntersecting) {
          this.handleImage();
          this.removeIntersectionObserver();
        }
      });

      this.io.observe(this.el.querySelector("img"));
    } else {
      // fall back to setTimeout for Safari and IE
      setTimeout(() => {
        this.handleImage();
      }, 300);
    }
  }

  private removeIntersectionObserver() {
    if (this.io) {
      this.io.disconnect();
      this.io = null;
    }
  }

  render() {
    return <img data-src={this.src} alt={this.alt}></img>;
  }
}
